import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { 
  getFacilityReport, 
  getFacilityReportByDate, 
  getFacilityReportBySport, 
  getFacilityReportBySportAndDate 
} from '../services/api';

// Async thunks for facility report data
export const fetchFacilityReport = createAsyncThunk(
  'facilityReport/fetchReport',
  async (facilityId, { rejectWithValue }) => {
    try {
      const response = await getFacilityReport(facilityId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch facility report');
    }
  }
);

export const fetchFacilityReportByDate = createAsyncThunk(
  'facilityReport/fetchByDate',
  async ({ facilityId, startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await getFacilityReportByDate(facilityId, startDate, endDate);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch facility report by date');
    }
  }
);

export const fetchFacilityReportBySport = createAsyncThunk(
  'facilityReport/fetchBySport',
  async ({ facilityId, sportId }, { rejectWithValue }) => {
    try {
      const response = await getFacilityReportBySport(facilityId, sportId);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch facility report by sport');
    }
  }
);

export const fetchFacilityReportBySportAndDate = createAsyncThunk(
  'facilityReport/fetchBySportAndDate',
  async ({ facilityId, sportId, startDate, endDate }, { rejectWithValue }) => {
    try {
      const response = await getFacilityReportBySportAndDate(facilityId, sportId, startDate, endDate);
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || 'Failed to fetch facility report by sport and date');
    }
  }
);

const initialState = {
  facilityReport: null,
  loading: false,
  error: null,
  selectedFacility: null,
  selectedSport: null,
  dateRange: {
    startDate: null,
    endDate: null
  }
};

const facilityReportSlice = createSlice({
  name: 'facilityReport',
  initialState,
  reducers: {
    setSelectedFacility: (state, action) => {
      state.selectedFacility = action.payload;
    },
    setSelectedSport: (state, action) => {
      state.selectedSport = action.payload;
    },
    setDateRange: (state, action) => {
      state.dateRange = action.payload;
    },
    clearFilters: (state) => {
      state.selectedFacility = null;
      state.selectedSport = null;
      state.dateRange = {
        startDate: null,
        endDate: null
      };
    }
  },
  extraReducers: (builder) => {
    builder
      // Fetch facility report
      .addCase(fetchFacilityReport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFacilityReport.fulfilled, (state, action) => {
        state.loading = false;
        state.facilityReport = action.payload;
      })
      .addCase(fetchFacilityReport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch by date
      .addCase(fetchFacilityReportByDate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFacilityReportByDate.fulfilled, (state, action) => {
        state.loading = false;
        state.facilityReport = action.payload;
      })
      .addCase(fetchFacilityReportByDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch by sport
      .addCase(fetchFacilityReportBySport.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFacilityReportBySport.fulfilled, (state, action) => {
        state.loading = false;
        state.facilityReport = action.payload;
      })
      .addCase(fetchFacilityReportBySport.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })
      // Fetch by sport and date
      .addCase(fetchFacilityReportBySportAndDate.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchFacilityReportBySportAndDate.fulfilled, (state, action) => {
        state.loading = false;
        state.facilityReport = action.payload;
      })
      .addCase(fetchFacilityReportBySportAndDate.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  setSelectedFacility, 
  setSelectedSport, 
  setDateRange, 
  clearFilters 
} = facilityReportSlice.actions;

export default facilityReportSlice.reducer; 